/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { TFunction } from 'i18next';
import { getApiErrorMessage } from './errors';

type AppleAuthErrorData = {
  code?: string;
  message?: string;
  failedLoginAttempt?: number | string;
};

export type AppleAuthFailure =
  | { kind: 'inline'; message: string }
  | { kind: 'cancelled' };

const APPLE_AUTH_CANCELLED_MARKERS = [
  'ERR_REQUEST_CANCELED',
  'ERR_CANCELED',
  'SIGN_IN_CANCELLED',
  'cancelled',
  'canceled',
];

// User dismissed the sheet — not an API failure.
export const isAppleAuthCancelled = (error: unknown): boolean => {
  if (error == null) return false;

  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code?: unknown }).code ?? '')
      : '';
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : String(error);

  const haystack = `${code} ${message}`.toLowerCase();
  return APPLE_AUTH_CANCELLED_MARKERS.some((marker) =>
    haystack.includes(marker.toLowerCase()),
  );
};

const parseFailedLoginAttempt = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const readAppleAuthErrorData = (error: unknown): AppleAuthErrorData => {
  const responseData = (error as { response?: { data?: AppleAuthErrorData } })
    ?.response?.data;
  if (responseData) {
    return responseData;
  }

  if (typeof error === 'object' && error !== null) {
    const direct = error as AppleAuthErrorData;
    if (direct.code || direct.message || direct.failedLoginAttempt != null) {
      return direct;
    }
  }

  return {};
};

// Maps Apple exchange errors to inline banner; cancellation is silent.
export function resolveAppleAuthFailure(
  error: unknown,
  t: TFunction,
  fallback: string,
): AppleAuthFailure {
  if (isAppleAuthCancelled(error)) {
    return { kind: 'cancelled' };
  }

  const generic = t('appleAuthErrors.genericError', { defaultValue: fallback });
  const data = readAppleAuthErrorData(error);
  const code = data.code?.trim().toUpperCase();

  if (!code) {
    return { kind: 'inline', message: generic };
  }

  if (code === 'USER_TERMINATED') {
    const failedLoginAttempt = parseFailedLoginAttempt(data.failedLoginAttempt);
    if (failedLoginAttempt === 0) {
      return {
        kind: 'inline',
        message: t('appleAuthErrors.userTerminatedMessage'),
      };
    }
    return { kind: 'inline', message: generic };
  }

  if (code === 'USER_LOCKED') {
    return {
      kind: 'inline',
      message: t('appleAuthErrors.userLockedMessage'),
    };
  }

  if (code === 'USER_SCHEDULED_FOR_DELETION') {
    return {
      kind: 'inline',
      message: getApiErrorMessage(error, generic),
    };
  }

  return { kind: 'inline', message: generic };
}
